// BoardDataSection.jsx
import React from "react";
import { Info, CheckCircle2, AlertCircle, Users } from "lucide-react";

/** Card + Section header: same visuals as other sections */
const Karta = ({ children, style }) => (
  <div
    style={{
      border: "1px solid #e6e9ee",
      borderRadius: 16,
      padding: 16,
      background: "#fff",
      boxShadow: "0 16px 48px rgba(16,24,40,0.16), 0 8px 22px rgba(16,24,40,0.12)",
      ...style,
    }}
  >
    {children}
  </div>
);

const Sekcja = ({ title, icon: Icon = Info, children }) => (
  <Karta>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      {Icon ? <Icon size={18} color="#0b74e5" /> : null}
      <h3 style={{ margin: 0, fontSize: 18, color: "#0b74e5", textDecorationThickness: "2px", textUnderlineOffset: 4 }}>
        {title}
      </h3>
    </div>
    {children}
  </Karta>
);

const StatusCard = ({ title, status, tone = "info", subtitle, pills = [] }) => {
  const tones = {
    ok:   { top: "#22c55e", icon: "#22c55e", text: "#111827", sub: "#6b7280", border: "#e6f6ec", Icon: CheckCircle2 },
    bad:  { top: "#ef4444", icon: "#ef4444", text: "#111827", sub: "#6b7280", border: "#fdecec", Icon: AlertCircle  },
    warn: { top: "#f59e0b", icon: "#f59e0b", text: "#111827", sub: "#6b7280", border: "#fef3c7", Icon: AlertCircle  },
    info: { top: "#3b82f6", icon: "#3b82f6", text: "#111827", sub: "#6b7280", border: "#dbeafe", Icon: Info        },
  };
  const c = tones[tone] || tones.info;
  const IconComp = c.Icon;

  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        background: "#fff",
        boxShadow: "0 6px 24px rgba(16,24,40,0.06)",
        overflow: "hidden",
        display: "grid",
      }}
    >
      <div style={{ height: 6, background: c.top }} />
      <div style={{ display: "grid", gridTemplateColumns: "56px 1fr", gap: 12, padding: 14 }}>
        <div
          style={{
            display: "grid",
            placeItems: "center",
            height: 56,
            width: 56,
            borderRadius: "999px",
            border: `2px solid ${c.icon}`,
            color: c.icon,
          }}
        >
          <IconComp size={28} />
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", color: "#6b7280", fontWeight: 700 }}>
            {title}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: c.text }}>{status}</div>
          {subtitle ? <div style={{ fontSize: 12, color: c.sub }}>{subtitle}</div> : null}
          {pills.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {pills.map((p, idx) => (
                <span
                  key={`${p}-${idx}`}
                  style={{
                    fontSize: 12,
                    padding: "4px 8px",
                    borderRadius: 999,
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    color: "#374151",
                  }}
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Helpers */
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const parseList = (raw) =>
  String(raw ?? "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const isAllNo = (arr) => arr.length > 0 && arr.every((v) => v.toLowerCase() === "nie");
const hasAnyYes = (arr) => arr.some((v) => v.toLowerCase() === "tak");

const isDateLike = (val) => /^\d{4}-\d{2}-\d{2}(?:t.*)?$/i.test(String(val ?? "").trim());
const fmtDate = (val) => {
  try {
    const d = new Date(String(val));
    return isNaN(d) ? String(val) : d.toLocaleDateString("pl-PL");
  } catch {
    return String(val ?? "—");
  }
};

/** Field-specific logic */
function mapBoardField(key, obj) {
  const title = obj?.label_short || key;
  const subtitle = obj?.label_long || "";
  const raw = obj?.value == null ? "" : String(obj.value).trim();

  if (!raw || raw === "-") {
    return { title, subtitle, status: "—", tone: "info", pills: [] };
  }

  // board_changes: date
  if (key === "board_changes" || /zmian/.test(title.toLowerCase())) {
    const status = isDateLike(raw) ? fmtDate(raw) : raw;
    return { title, subtitle, status, tone: "info", pills: [] };
  }

  // Ages: list of years
  if (key === "board_age") {
    const list = parseList(raw);
    return { title, subtitle, status: list.length ? "Lata urodzenia" : "—", tone: "info", pills: list };
  }

  // Activity: numeric list -> sum
  if (key === "board_activity") {
    const nums = parseList(raw).map((x) => Number(x.replace(",", ".")).valueOf());
    const valid = nums.filter((n) => Number.isFinite(n));
    const total = valid.reduce((a, b) => a + b, 0);
    let tone = "ok";
    if (total === 0) tone = "ok";
    else if (total <= 2) tone = "info";
    else if (total <= 5) tone = "warn";
    else tone = "bad";
    return {
      title,
      subtitle,
      status: String(total),
      tone,
      pills: parseList(raw), // show original list as chips
    };
  }

  // PEP / Sanctions / History: list of yes/no
  if (key === "board_pep" || key === "board_sanction_list" || key === "board_history") {
    const list = parseList(raw);
    if (hasAnyYes(list)) {
      return { title, subtitle, status: "Tak", tone: "bad", pills: list };
    }
    if (isAllNo(list)) {
      return { title, subtitle, status: "Nie", tone: "ok", pills: list };
    }
    // mixed/unknown
    return { title, subtitle, status: cap(raw), tone: "info", pills: list };
  }

  // Control: yes/no (neutral by default)
  if (key === "board_control") {
    const low = raw.toLowerCase();
    if (low === "tak") return { title, subtitle, status: "Tak", tone: "info", pills: [] }; // change to 'warn' if you prefer
    if (low === "nie") return { title, subtitle, status: "Nie", tone: "info", pills: [] };
    return { title, subtitle, status: cap(raw), tone: "info", pills: [] };
  }

  // Default: just show the value
  return { title, subtitle, status: cap(raw), tone: "info", pills: [] };
}

/** Section: Board Data (cards) */
export default function BoardDataSection({ data = {} }) {
  const entries = Object.entries(data).map(([key, obj]) => ({ key, ...mapBoardField(key, obj) }));

  if (entries.length === 0) {
    return (
      <Sekcja title="Zarząd">
        <em>Brak danych.</em>
      </Sekcja>
    );
  }

  return (
    <Sekcja title="Zarząd" icon={Users}>
      <style>{`
        .boardGrid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }
        @media (max-width: 1280px) { .boardGrid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 1024px) { .boardGrid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .boardGrid { grid-template-columns: 1fr; } }
      `}</style>

      <div className="boardGrid">
        {entries.map(({ key, title, status, tone, subtitle, pills }) => (
          <StatusCard
            key={key}
            title={title}
            status={status}
            tone={tone}
            subtitle={subtitle}
            pills={pills}
          />
        ))}
      </div>
    </Sekcja>
  );
}
